import { useState, useRef, useEffect } from "react";
import { ChevronDown, ChevronRight, Pause, Play, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import axios from "@/lib/axios";

const CallRecording = ({ call }) => {
    const { id, file_name: fileName } = call;
    const [recordingUrl, setRecordingUrl] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isRecordingExpanded, setIsRecordingExpanded] = useState(false);
    const { toast } = useToast();
    const audioRef = useRef(null);

    const handlePlay = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (recordingUrl) {
            togglePlayback(e);
            return;
        }
        
        setIsLoading(true);
        try {
            const response = await axios.get(`/calls/${id}/recording`);
            if (response.data.recording_url) {
                setRecordingUrl(response.data.recording_url);
            }
        } catch (error) {
            console.error('Error fetching MP3 URL:', error);
            toast({
                title: "Error",
                description: "Failed to load the MP3 file.",
                variant: "destructive",
            });
        } finally {
            setIsLoading(false);
        }
    };

    const togglePlayback = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!audioRef.current) return;
        if (isPlaying) {
            audioRef.current.pause();
            setIsPlaying(false);
        } else {
            audioRef.current.play();
            setIsPlaying(true);
        }
    };

    const handleSeek = (e) => {
        const newTime = Number.parseInt(e.target.value);
        setCurrentTime(newTime);
        if (audioRef.current) {
            audioRef.current.currentTime = newTime;
        }
    };

    const handleTimeUpdate = () => {
        setCurrentTime(audioRef.current.currentTime);
    };

    const handleLoadedMetadata = () => {
        setDuration(audioRef.current.duration);
    };

    const handleAudioPlay = () => setIsPlaying(true);
    const handleAudioPause = () => setIsPlaying(false);
    const handleAudioEnded = () => {
        setIsPlaying(false);
        setCurrentTime(0);
    };

    // Auto-play once recordingUrl is set
    useEffect(() => {
        if (recordingUrl && audioRef.current) {
            audioRef.current.play().catch((err) => {
                console.warn("Autoplay blocked or failed:", err);
            });
        }
    }, [recordingUrl]);

    const formatTime = (seconds) => {
        if (!seconds || isNaN(seconds)) return "0:00";
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, "0")}`;
    };

    if (!fileName) return null;

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <button
                onClick={() => setIsRecordingExpanded(!isRecordingExpanded)}
                className="flex items-center justify-between w-full text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded"
                aria-expanded={isRecordingExpanded}
            >
                <h2 className="text-sm font-semibold text-gray-900">Call Recording</h2>
                {isRecordingExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </button>

            {isRecordingExpanded && (
                <div className="mt-4 max-w-lg">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={handlePlay}
                            disabled={isLoading}
                            className="flex items-center justify-center w-10 h-10 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            aria-label={isPlaying ? "Pause recording" : "Play recording"}
                        >
                            {isLoading ? (
                                <Loader2 size={16} className="animate-spin" />
                            ) : isPlaying ? (
                                <Pause size={16} />
                            ) : (
                                <Play size={16} />
                            )}
                        </button>

                        <div className="flex-1 flex items-center gap-3">
                            <span className="text-xs text-gray-500 font-mono tabular-nums w-10">{formatTime(currentTime)}</span>

                            <div className="flex-1 relative">
                                <div className="w-full h-2 bg-gray-200 rounded-full">
                                    <div
                                        className="h-2 bg-blue-500 rounded-full transition-all duration-300"
                                        style={{ width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%` }}
                                    ></div>
                                </div>
                                <input
                                    type="range"
                                    min="0"
                                    max={duration || 0}
                                    value={currentTime}
                                    onChange={handleSeek}
                                    className="absolute inset-0 w-full h-2 opacity-0 cursor-pointer"
                                    aria-label="Seek audio position"
                                    aria-valuetext={`${formatTime(currentTime)} of ${formatTime(duration)}`}
                                />
                            </div>

                            <span className="text-xs text-gray-500 font-mono tabular-nums w-10">{formatTime(duration)}</span>
                        </div>
                    </div>
                    
                    {recordingUrl && (
                        <audio 
                            ref={audioRef} 
                            src={recordingUrl} 
                            preload="auto"
                            onPlay={handleAudioPlay}
                            onPause={handleAudioPause}
                            onTimeUpdate={handleTimeUpdate}
                            onLoadedMetadata={handleLoadedMetadata}
                            onEnded={handleAudioEnded}
                        />
                    )}
                </div>
            )}
        </div>
    );
};

export default CallRecording;